import { api } from './client';

/**
 * Paying for a booking online, through Razorpay.
 *
 * ---------------------------------------------------------------------------
 * THE ORDER OF OPERATIONS IS THE WHOLE THING
 * ---------------------------------------------------------------------------
 * There are three calls and they must happen in this order:
 *
 *   1. the booking is placed          `POST /checkout`  → status awaiting_payment
 *   2. `openCheckout(bookingId)`      `POST /bookings/:id/payment/order`
 *   3. Razorpay's SDK collects money  (the only step not on this server)
 *   4. `confirmCheckout(...)`         `POST /bookings/:id/payment/verify`
 *
 * A booking has to exist before there is anything to pay for - the amount is
 * read off the booking server-side, which is the entire reason step 2 exists
 * rather than the app naming a price. So an online booking is **created
 * unpaid**, sits in `awaiting_payment`, and only reaches dispatch once step 4
 * (or the webhook) lands.
 *
 * The app used to do this backwards: it ran a stand-in gateway, then created
 * the booking on success. That could never have worked against a real gateway,
 * because step 2 needs an id step 1 had not produced yet.
 *
 * ---------------------------------------------------------------------------
 * STEP 4 IS NOT WHAT MAKES A BOOKING PAID
 * ---------------------------------------------------------------------------
 * The server also receives a `payment.captured` webhook straight from
 * Razorpay, and that is the authoritative path. `confirmCheckout` exists to
 * make the app's own wait short - without it the customer would stare at a
 * spinner until a webhook they cannot see arrives.
 *
 * Which means a failure here is **not** a failed payment. If the app dies
 * between the SDK returning and this call, the webhook still completes the
 * booking. Anything reading a `confirmCheckout` rejection as "the money did
 * not go through" will tell a customer their paid booking failed - see
 * `describeConfirmFailure`.
 *
 * ---------------------------------------------------------------------------
 * ONE PAYMENT PER BASKET
 * ---------------------------------------------------------------------------
 * A basket is paid for as ONE gateway order - the `checkouts/:id/payment`
 * routes below - and it is paid BEFORE anything is booked: the checkout waits,
 * with no booking, until Razorpay captures the money, and the server books
 * every line then. The per-booking routes stay for paying a single booking
 * left unpaid by an older version of the app.
 */

/** What Razorpay Checkout needs, minted by the server. */
export type CheckoutHandoff = {
  /** Our own `Order` row id. Not the gateway's. */
  orderId: string;
  /** The gateway's order id - this is what the SDK is opened with. */
  razorpayOrderId: string;
  /** The PUBLISHABLE key. The secret never leaves the server. */
  keyId: string;
  /** Decimal rupees, e.g. `"599.00"`. The SDK wants paise - see `toPaise`. */
  amount: string;
  currency: string;
  /** `booking` | `subscription`. */
  purpose: string;
  /** Booking number or plan name. Shown on the sheet. */
  reference: string;
  bookingNumber: string | null;
  customerName: string | null;
  customerContact: string | null;
};

/** What the SDK hands back when the customer has paid. */
export type CheckoutResult = {
  razorpayOrderId: string;
  razorpayPaymentId: string;
  signature: string;
};

/** The server's own record of the payment, after it has verified it. */
export type PaidOrder = {
  id: string;
  razorpayOrderId: string;
  amount: string;
  amountPaid: string;
  status: string;
};

/**
 * Rupee string to integer paise, which is what Razorpay transacts in.
 *
 * `Math.round` rather than a truncation: `"599.00"` is exactly 59900 in
 * decimal but not in binary, and `599.00 * 100` can land on 59899.999999999996
 * - truncating that undercharges by a paisa and the gateway rejects the
 * amount mismatch. The server does the same conversion in `payments.money.ts`
 * and the two must agree exactly or every order is refused.
 */
export const toPaise = (rupees: string): number =>
  Math.round(Number(rupees) * 100);

/**
 * Step 2 - mint the gateway order.
 *
 * Safe to call again: the server returns an order still inside its validity
 * window rather than creating a second one, and reissues an expired one. So a
 * customer who backs out of the sheet and taps Pay again does not leave a
 * trail of abandoned orders.
 */
export const openCheckout = (bookingId: string): Promise<CheckoutHandoff> =>
  api.post<CheckoutHandoff>(
    `/bookings/${encodeURIComponent(bookingId)}/payment/order`,
  );

/**
 * Step 4 - tell the server what the SDK said.
 *
 * The server checks the signature, then independently fetches the payment from
 * Razorpay and asserts its status, order and amount before anything moves. A
 * client saying "it worked" is never enough, which is why this returns the
 * server's own view of the order rather than an acknowledgement.
 */
export const confirmCheckout = (
  bookingId: string,
  result: CheckoutResult,
): Promise<PaidOrder> =>
  api.post<PaidOrder>(
    `/bookings/${encodeURIComponent(bookingId)}/payment/verify`,
    result,
  );

/** Where a booking stands on money, for either payment mode. */
export type BookingPayment = {
  paymentMode: 'online' | 'cash';
  paymentStatus: string;
  flatPrice: string;
  cashCollectedAmount: string | null;
  cashCollectedAt: string | null;
};

/**
 * The fallback read, for when the app lost track.
 *
 * Killed mid-payment, or `confirmCheckout` failed on a flaky connection: the
 * webhook may well have completed the booking regardless. This is how the app
 * finds out, rather than assuming the worst and showing a failure over a
 * payment that went through.
 */
export const paymentStatus = (bookingId: string): Promise<BookingPayment> =>
  api.get<BookingPayment>(
    `/bookings/${encodeURIComponent(bookingId)}/payment`,
  );

/**
 * Step 2 for a whole basket - one gateway order for every online booking the
 * checkout placed. Safe to call again, like `openCheckout`: an order still
 * valid for the same bookings comes back rather than a second one.
 */
export const openBasketCheckout = (
  checkoutId: string,
): Promise<CheckoutHandoff> =>
  api.post<CheckoutHandoff>(
    `/checkouts/${encodeURIComponent(checkoutId)}/payment/order`,
  );

/** Step 4 for a whole basket. Marks every booking it covered paid. */
export const confirmBasketCheckout = (
  checkoutId: string,
  result: CheckoutResult,
): Promise<PaidOrder> =>
  api.post<PaidOrder>(
    `/checkouts/${encodeURIComponent(checkoutId)}/payment/verify`,
    result,
  );

/** Where a basket stands on money. */
export type BasketPayment = {
  checkoutId: string;
  /** No booking exists until this is `placed`. */
  status: 'awaiting_payment' | 'paid' | 'placed' | 'expired';
  paymentStatus: 'paid' | 'partially_paid' | 'unpaid';
  paidCount: number;
  total: number;
  /** The bookings the paid basket became. Empty until it is booked. */
  bookings: { id: string; bookingNumber: string; status: string }[];
};

/** The basket twin of `paymentStatus`, for when the app lost track. */
export const basketPaymentStatus = (
  checkoutId: string,
): Promise<BasketPayment> =>
  api.get<BasketPayment>(
    `/checkouts/${encodeURIComponent(checkoutId)}/payment`,
  );
